/**
 * clearCognitoCookies - Utility to clear Cognito-related cookies
 * 
 * SECURITY:
 * This function attempts to clear any Cognito cookies that may have been
 * set by previous versions of the app or by the main site.
 * 
 * Note: We can only clear cookies for domains we have access to.
 * Cookies on parent domains (like .tbd.health) can only be cleared
 * if we're on a subdomain of that domain.
 */

/**
 * Get all cookies as an object
 */
const getCookies = () => {
    const cookies = {};
    document.cookie.split(';').forEach(cookie => {
        const [name, value] = cookie.trim().split('=');
        if (name) {
            cookies[name] = value;
        }
    });
    return cookies;
};

/**
 * Delete a cookie by name
 * Attempts to delete from multiple paths and domains
 */
const deleteCookie = (name) => {
    const domains = [
        '', // Current domain
        window.location.hostname,
        '.' + window.location.hostname,
        '.tbd.health',
        '.tbdhealth.xyz'
    ];
    
    const paths = ['/', ''];
    
    domains.forEach(domain => {
        paths.forEach(path => {
            // Try to delete with various combinations
            const domainPart = domain ? `; domain=${domain}` : '';
            const pathPart = path ? `; path=${path}` : '';
            
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainPart}${pathPart}`;
            document.cookie = `${name}=; max-age=0${domainPart}${pathPart}`;
        });
    });
};

/**
 * Clear all Cognito-related cookies
 * 
 * Cognito cookies typically have these patterns:
 * - CognitoIdentityServiceProvider.*
 * - amplify-*
 * 
 * @returns {string[]} Array of cookie names that were attempted to be cleared
 */
export const clearCognitoCookies = () => {
    const cookies = getCookies();
    const clearedCookies = [];
    
    Object.keys(cookies).forEach(name => {
        // Match Cognito-related cookie patterns
        if (
            name.startsWith('CognitoIdentityServiceProvider') ||
            name.startsWith('amplify-') ||
            name.includes('Cognito') ||
            name.includes('cognito')
        ) {
            deleteCookie(name);
            clearedCookies.push(name);
            console.log(`[Security] Cleared cookie: ${name}`);
        }
    });
    
    if (clearedCookies.length > 0) {
        console.log(`[Security] Cleared ${clearedCookies.length} Cognito cookies`);
    }
    
    return clearedCookies;
};

/**
 * Clear all cookies (nuclear option)
 * Use with caution - this will clear ALL cookies accessible to this page
 */
export const clearAllCookies = () => {
    const cookies = getCookies();
    
    Object.keys(cookies).forEach(name => {
        deleteCookie(name);
    });
    
    console.log(`[Security] Cleared all accessible cookies`);
};

export default clearCognitoCookies;
